import { Flex, Text, Tooltip } from '@chakra-ui/react';
import React from 'react';

interface PracticeStatusBadgeProps {
  active: boolean | null | undefined; // Boolean indicating practice status
  practiceOnPac?: boolean | string | null | undefined; // Practice-level PAC status (all providers on PAC)
}

const PracticeStatusBadge: React.FC<PracticeStatusBadgeProps> = ({
  active,
  practiceOnPac,
}) => {
  // Active/Inactive Badge
  const getActiveBadge = () => {
    if (active === null || active === undefined) {
      return null;
    }

    const { status, bgColor, tooltipLabel } = !active
      ? {
          status: 'Inactive' as const,
          bgColor: '#E53E3E',
          tooltipLabel:
            'Practice is temporarily closed or not accepting new patients',
        }
      : {
          status: 'Active' as const,
          bgColor: '#38A169',
          tooltipLabel:
            'Practice is currently operational and accepting patients',
        };

    return (
      <Tooltip label={tooltipLabel} placement="top" hasArrow>
        <Flex
          bg={bgColor}
          borderRadius={'0.3125rem'}
          minH="1.5rem"
          alignItems={'center'}
          justifyContent={'center'}
          px="0.5rem"
          py="0.25rem"
          cursor="help"
          _hover={{
            filter: 'brightness(1.05)',
            transform: 'translateY(-1px)',
          }}
          transition="filter 0.15s ease, transform 0.15s ease"
          data-testid="chip"
        >
          <Text
            color="white"
            fontSize="0.75rem"
            fontWeight={600}
            lineHeight="1.2"
            textAlign="center"
            whiteSpace="normal"
            wordBreak="break-word"
            minW="4.5rem"
          >
            {status}
          </Text>
        </Flex>
      </Tooltip>
    );
  };

  // PAC Status Badge - only show when data exists
  const getPacBadge = () => {
    // Return null if practiceOnPac doesn't exist (undefined, null, or empty string)
    if (
      practiceOnPac === undefined ||
      practiceOnPac === null ||
      practiceOnPac === ''
    ) {
      return null;
    }

    // Normalize the value to handle both boolean and string types
    const normalizedPacStatus =
      practiceOnPac === true || practiceOnPac === 'true'
        ? true
        : practiceOnPac === false || practiceOnPac === 'false'
        ? false
        : null;

    // Only show PAC badge when we have valid data
    if (normalizedPacStatus === null) {
      return null;
    }

    const { status, bgColor, tooltipLabel } =
      normalizedPacStatus === false
        ? {
            status: 'Not on PAC' as const,
            bgColor: '#E53E3E',
            tooltipLabel:
              'All providers in this practice are not on PAC',
          }
        : {
            status: 'On PAC' as const,
            bgColor: '#38A169',
            tooltipLabel: 'All providers in this practice are on PAC',
          };

    return (
      <Tooltip label={tooltipLabel} placement="top" hasArrow>
        <Flex
          bg={bgColor}
          borderRadius={'0.3125rem'}
          minH="1.5rem"
          alignItems={'center'}
          justifyContent={'center'}
          px="0.5rem"
          py="0.25rem"
          cursor="help"
          _hover={{
            filter: 'brightness(1.05)',
            transform: 'translateY(-1px)',
          }}
          transition="filter 0.15s ease, transform 0.15s ease"
          data-testid="chip"
        >
          <Text
            color="white"
            fontSize="0.75rem"
            fontWeight={600}
            lineHeight="1.2"
            textAlign="center"
            whiteSpace="normal"
            wordBreak="break-word"
            minW="4.5rem"
          >
            {status}
          </Text>
        </Flex>
      </Tooltip>
    );
  };

  return (
    <Flex gap="0.5rem" alignItems="center">
      {getActiveBadge()}
      {getPacBadge()}
    </Flex>
  );
};

export default PracticeStatusBadge;